/**
 * Site chrome: skip link, DEMO banner, sticky header with the services mega
 * menu, the mobile menu and the footer.
 *
 * The mock-up opens the mega menu on hover only. Hover is kept, but the trigger
 * is a real button with `aria-expanded` that also opens on click and closes on
 * Escape/focus-out, so the menu is reachable by keyboard and announced correctly
 * (the mock-up's markup would fail the a11y suite otherwise).
 */
import type { ThemePreference } from "@mb-group/shared-types";
import { el } from "../core/dom.js";
import type { AppContext } from "../core/context.js";
import type {
	Footer as FooterData,
	Navigation as NavData,
	ServiceCategory,
} from "../data/types.js";
import type { AppRouteKey } from "../core/routes.js";
import { applyTheme, storePreference } from "../core/theme.js";

export function skipLink(ctx: AppContext): HTMLElement {
	return el("a", { class: "mb-skiplink", href: "#mb-main" }, ctx.t("common.skipToContent"));
}

/** The banner shown while the gateway is unreachable and DEMO content is served. */
export function demoBanner(ctx: AppContext): HTMLElement {
	const copy = ctx.copy().demo;
	return el(
		"div",
		{ class: "mb-demobanner", role: "note", hidden: !ctx.store.get().demo },
		el(
			"div",
			{ class: "mb-demobanner__inner" },
			el("span", { class: "mb-demobanner__badge" }, ctx.t("common.demoBadge")),
			el(
				"span",
				{ class: "mb-demobanner__text" },
				el("strong", null, `${copy.bannerTitle}. `),
				copy.bannerBody,
			),
		),
	);
}

function logo(ctx: AppContext, size: "header" | "footer"): HTMLElement {
	return el(
		"a",
		{
			class: `mb-logo mb-logo--${size}`,
			href: ctx.href("home"),
			"aria-label": ctx.copy().brand.name,
		},
		el("img", {
			src: "/static/logo.png",
			alt: ctx.copy().brand.name,
			width: "160",
			height: "26",
			decoding: "async",
		}),
	);
}

/** Anchor into the services catalogue for a given category. */
function categoryHref(ctx: AppContext, category: ServiceCategory): string {
	return `${ctx.href("services")}#kategoria-${category.slug}`;
}

// --- utility controls -------------------------------------------------------

/**
 * PL/EN switch. The approved mock-up is Polish-only and shows no such control,
 * but the platform serves both locales (localized paths, hreflang, CMS content
 * per locale), so the site needs a visible way to change language.
 *
 * Switching re-navigates the current route under the other locale's path, which
 * is what keeps the URL, the chrome and the content in step.
 */
export function localeSwitch(ctx: AppContext): HTMLElement {
	const current = ctx.locale();
	const group = el("div", {
		class: "mb-langswitch",
		role: "group",
		"aria-label": ctx.t("switcher.language"),
	});

	for (const locale of ["pl", "en"] as const) {
		const label = locale === "pl" ? ctx.t("switcher.polish") : ctx.t("switcher.english");
		group.appendChild(
			el(
				"button",
				{
					class: `mb-langswitch__button${locale === current ? " is-active" : ""}`,
					type: "button",
					lang: locale,
					"aria-pressed": String(locale === current),
					title: label,
					onClick: () => {
						if (locale === current) return;
						ctx.router.switchLocale(locale);
					},
				},
				locale.toUpperCase(),
			),
		);
	}
	return group;
}

/**
 * Light/dark/system theme control. A native <select> keeps it to one compact
 * control (the header is already busy) while staying keyboard- and
 * screen-reader-friendly without custom ARIA.
 */
export function themeSwitch(ctx: AppContext): HTMLElement {
	const select = el("select", {
		class: "mb-themeswitch",
		"aria-label": ctx.t("switcher.theme"),
	});

	const current = ctx.store.get().themePreference;
	const options: [ThemePreference, string][] = [
		["light", ctx.t("switcher.light")],
		["dark", ctx.t("switcher.dark")],
		["system", ctx.t("switcher.system")],
	];
	for (const [value, label] of options) {
		select.appendChild(
			el("option", { value, ...(value === current ? { selected: true } : {}) }, label),
		);
	}

	select.addEventListener("change", () => {
		const preference = select.value as ThemePreference;
		storePreference(preference);
		const mode = applyTheme(preference);
		ctx.store.set({ themePreference: preference, theme: mode });
		ctx.track({ type: "theme_change", preference });
	});

	return select;
}

// --- header -----------------------------------------------------------------

const PRIMARY_LINKS: { key: AppRouteKey; label: (ctx: AppContext) => string }[] = [
	{ key: "industries", label: (ctx) => ctx.copy().nav.industries },
	{ key: "caseStudies", label: (ctx) => ctx.copy().nav.cases },
	{ key: "knowledge", label: (ctx) => ctx.copy().nav.knowledge },
	{ key: "about", label: (ctx) => ctx.copy().nav.about },
];

export function header(ctx: AppContext, _nav: NavData, categories: ServiceCategory[]): HTMLElement {
	const copy = ctx.copy();
	const state = ctx.store.get();

	const megaPanel = megaMenu(ctx, categories);
	const megaTrigger = el(
		"button",
		{
			class: "mb-nav__trigger",
			type: "button",
			"aria-expanded": String(state.megaOpen),
			"aria-controls": "mb-mega",
			onClick: () => ctx.store.set({ megaOpen: !ctx.store.get().megaOpen }),
		},
		copy.nav.services,
		el("span", { class: "mb-nav__chevron", "aria-hidden": "true" }),
	);

	const megaWrap = el(
		"div",
		{
			class: "mb-nav__mega",
			onMouseenter: () => ctx.store.set({ megaOpen: true }),
			onMouseleave: () => ctx.store.set({ megaOpen: false }),
		},
		megaTrigger,
		megaPanel,
	);

	const desktopNav = el(
		"nav",
		{ class: "mb-nav", "aria-label": copy.a11y.mainNav },
		megaWrap,
		...PRIMARY_LINKS.map((link) =>
			el(
				"a",
				{ class: "mb-nav__link", href: ctx.href(link.key), "data-routekey": link.key },
				link.label(ctx),
			),
		),
	);

	const searchButton = el(
		"button",
		{
			class: "mb-iconbutton",
			type: "button",
			"aria-label": copy.nav.search,
			onClick: () => ctx.openAssistant(),
		},
		el("span", { class: "mb-iconbutton__glass", "aria-hidden": "true" }),
	);

	const actions = el(
		"div",
		{ class: "mb-header__actions" },
		localeSwitch(ctx),
		themeSwitch(ctx),
		searchButton,
		el(
			"a",
			{
				class: "mb-cta mb-cta--accent mb-cta--compact",
				href: ctx.href("contact"),
				"data-routekey": "contact",
			},
			copy.nav.consult,
		),
	);

	const burger = el(
		"button",
		{
			class: "mb-burger",
			type: "button",
			"aria-label": copy.nav.menu,
			"aria-expanded": String(state.menuOpen),
			"aria-controls": "mb-mobilemenu",
			onClick: () => ctx.store.set({ menuOpen: !ctx.store.get().menuOpen }),
		},
		el("span", { "aria-hidden": "true" }),
		el("span", { "aria-hidden": "true" }),
		el("span", { "aria-hidden": "true" }),
	);

	const bar = el(
		"div",
		{ class: "mb-header__bar" },
		logo(ctx, "header"),
		desktopNav,
		actions,
		burger,
	);

	const root = el(
		"header",
		{ class: "mb-header", "data-mega-open": String(state.megaOpen) },
		bar,
		mobileMenu(ctx, categories),
	);

	// Escape closes whichever menu is open; focus-out closes the mega panel.
	root.addEventListener("keydown", (event) => {
		if (event.key !== "Escape") return;
		const current = ctx.store.get();
		if (current.megaOpen || current.menuOpen) {
			ctx.store.set({ megaOpen: false, menuOpen: false });
			megaTrigger.focus();
		}
	});
	root.addEventListener("focusout", (event) => {
		const next = event.relatedTarget;
		if (next instanceof Node && megaWrap.contains(next)) return;
		if (ctx.store.get().megaOpen) ctx.store.set({ megaOpen: false });
	});

	return root;
}

function megaMenu(ctx: AppContext, categories: ServiceCategory[]): HTMLElement {
	const copy = ctx.copy();

	const columns = el("div", { class: "mb-mega__grid" });
	for (const category of categories) {
		const items = el("ul", { class: "mb-mega__services" });
		for (const service of category.services) {
			items.appendChild(
				el(
					"li",
					null,
					el(
						"a",
						{ class: "mb-mega__service", href: ctx.href("serviceDetail", { slug: service.slug }) },
						service.title,
					),
				),
			);
		}
		columns.appendChild(
			el(
				"div",
				{ class: "mb-mega__col" },
				el(
					"a",
					{ class: "mb-mega__cat", href: categoryHref(ctx, category) },
					el("span", { class: "mb-mega__num" }, category.icon ?? ""),
					el("span", { class: "mb-mega__cattitle" }, category.title),
				),
				items,
			),
		);
	}

	return el(
		"div",
		{ class: "mb-mega", id: "mb-mega" },
		el(
			"div",
			{ class: "mb-mega__panel" },
			el(
				"div",
				{ class: "mb-mega__head" },
				el("p", { class: "mb-eyebrow" }, copy.nav.megaEyebrow),
				el("a", { class: "mb-inlinelink", href: ctx.href("services") }, copy.nav.fullPortfolio),
			),
			columns,
		),
	);
}

function mobileMenu(ctx: AppContext, categories: ServiceCategory[]): HTMLElement {
	const copy = ctx.copy();

	const catBlocks = categories.map((category) =>
		el(
			"div",
			{ class: "mb-mobilemenu__cat" },
			el(
				"a",
				{ class: "mb-mobilemenu__cattitle", href: categoryHref(ctx, category) },
				category.title,
			),
			el(
				"div",
				{ class: "mb-mobilemenu__chips" },
				...category.services.map((service) =>
					el(
						"a",
						{
							class: "mb-mobilemenu__chip",
							href: ctx.href("serviceDetail", { slug: service.slug }),
						},
						service.title,
					),
				),
			),
		),
	);

	return el(
		"div",
		{ class: "mb-mobilemenu", id: "mb-mobilemenu", hidden: !ctx.store.get().menuOpen },
		el("div", { class: "mb-mobilemenu__inner" }, ...catBlocks, mobileLinks(ctx, copy)),
	);
}

function mobileLinks(ctx: AppContext, copy: ReturnType<AppContext["copy"]>): HTMLElement {
	return el(
		"div",
		{ class: "mb-mobilemenu__links" },
		...PRIMARY_LINKS.map((link) =>
			el("a", { class: "mb-mobilemenu__link", href: ctx.href(link.key) }, link.label(ctx)),
		),
		el(
			"a",
			{ class: "mb-cta mb-cta--accent mb-mobilemenu__cta", href: ctx.href("contact") },
			copy.nav.consult,
		),
		// The desktop utility row is hidden on narrow viewports, so language and
		// theme need a home inside the drawer too.
		el("div", { class: "mb-mobilemenu__utility" }, localeSwitch(ctx), themeSwitch(ctx)),
	);
}

// --- footer -----------------------------------------------------------------

export function footer(
	ctx: AppContext,
	data: FooterData,
	categories: ServiceCategory[],
): HTMLElement {
	const copy = ctx.copy();

	const brandCol = el(
		"div",
		{ class: "mb-footer__brand" },
		logo(ctx, "footer"),
		el("p", { class: "mb-footer__blurb" }, copy.brand.blurb),
		el("p", { class: "mb-footer__note" }, copy.brand.mockupNote),
	);

	const servicesCol = el(
		"div",
		{ class: "mb-footer__col" },
		el("h2", { class: "mb-footer__heading" }, copy.footer.servicesTitle),
		el(
			"ul",
			{ class: "mb-footer__list" },
			...categories.map((category) =>
				el("li", null, el("a", { href: categoryHref(ctx, category) }, category.title)),
			),
		),
	);

	const groupCol = el(
		"div",
		{ class: "mb-footer__col" },
		el("h2", { class: "mb-footer__heading" }, copy.footer.groupTitle),
		el(
			"ul",
			{ class: "mb-footer__list" },
			...(
				[
					["about", copy.nav.about],
					["industries", copy.nav.industries],
					["caseStudies", copy.nav.cases],
					["knowledge", copy.nav.knowledge],
					["contact", copy.nav.contact],
				] as [AppRouteKey, string][]
			).map(([key, label]) =>
				el("li", null, el("a", { href: ctx.href(key), "data-routekey": key }, label)),
			),
		),
	);

	const contactCol = el(
		"div",
		{ class: "mb-footer__col" },
		el("h2", { class: "mb-footer__heading" }, copy.footer.contactTitle),
		el(
			"p",
			{ class: "mb-footer__locations" },
			...copy.footer.locations.split("\n").flatMap((line, i, all) => {
				const nodes: (HTMLElement | string)[] = [line];
				if (i < all.length - 1) nodes.push(el("br"));
				return nodes;
			}),
		),
		el(
			"a",
			{ class: "mb-cta mb-cta--accent mb-cta--compact", href: ctx.href("contact") },
			copy.nav.consult,
		),
	);

	const legal = el(
		"div",
		{ class: "mb-footer__legal" },
		el("p", { class: "mb-footer__copyright" }, data.copyright),
		el(
			"ul",
			{ class: "mb-footer__legallinks" },
			...data.legalLinks.map((link) => el("li", null, el("a", { href: link.href }, link.label))),
		),
	);

	return el(
		"footer",
		{ class: "mb-footer" },
		el("div", { class: "mb-footer__grid" }, brandCol, servicesCol, groupCol, contactCol),
		legal,
	);
}

/**
 * Maps a detail route to the nav item that should read as active, so
 * /uslugi/<slug> highlights "Usługi".
 */
export function parentKey(key: AppRouteKey): AppRouteKey {
	switch (key) {
		case "serviceDetail":
			return "services";
		case "caseStudyDetail":
			return "caseStudies";
		case "article":
			return "knowledge";
		case "companyDetail":
			return "companies";
		default:
			return key;
	}
}
